import React, { FunctionComponent } from 'react';
import { faCopy, faBan, faTrash, faPen } from '@fortawesome/pro-regular-svg-icons';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import PetTypeTags from 'components/UI/V3/PetTypeTags';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { Service, isBoardingService } from 'model/Service';
import { Room } from '@spike/room-model';
import styles from './BoardingServiceRow.module.scss';
import { MarketplaceTax } from '@spike/marketplace-model';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface BoardingServiceRowProps {
    service: Service;
    rooms: Room[];
    isLast: boolean;
    isMobile: boolean;
    onEdit?: (service: Service) => void;
    onDuplicate?: (service: Service) => void;
    onDeactivate?: (service: Service) => void;
    onDelete?: (service: Service) => void;
}

const BoardingServiceRow: FunctionComponent<BoardingServiceRowProps> = ({
    service,
    rooms,
    isLast,
    isMobile,
    onEdit,
    onDuplicate,
    onDeactivate,
    onDelete
}) => {
    const { getOrganizationMarketplace } = useOrganizationMarketplaces();

    // Access boarding metadata directly from service.boardingData
    const pricingOption = isBoardingService(service) ? 'Per Night' : 'unknown';

    // Get first price as display price (could be enhanced to show price range)
    const displayPrice = service.pricing.fixedPrice?.[0]?.price || 0;

    // Use service petTypes directly
    const petTypes = service.petTypes;

    const marketplaceId = service.marketplaceId ?? service.marketplaces[0]?.marketplaceId;
    const marketplaceName = marketplaceId != null ? getOrganizationMarketplace(marketplaceId)?.name : undefined;

    // Check if service is active
    const isActive = service.status.id === 1;

    // Get room name from room ID or unit ID (selectedRooms can contain either room IDs or unit IDs)
    const getRoomName = (selectedRoomId: string | undefined): string => {
        if (!selectedRoomId) return '-';

        // First, try to find as a room ID
        const room = rooms.find(r => r.id?.toString() === selectedRoomId.toString());
        if (room) {
            return room.name;
        }

        // If not found as room, try to find as a unit ID within any room
        for (const roomItem of rooms) {
            if (roomItem.units) {
                const unit = roomItem.units.find(u => u.id?.toString() === selectedRoomId.toString());
                if (unit) {
                    return `${roomItem.name} - ${unit.name}`;
                }
            }
        }

        // If nothing is found, return dash
        return 'Unknown room';
    };

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`;
    };

    const getTaxesDisplay = (taxes: Array<MarketplaceTax>) => {
        if (taxes.length === 0) {
            return 'No Taxable';
        }
        return taxes.length === 1 ? `${taxes.length} tax` : `${taxes.length} taxes`;
    };

    const editOption = {
        id: 'edit',
        label: 'Edit',
        icon: faPen,
        onClick: () => onEdit?.(service)
    };

    const duplicateOption = {
        id: 'duplicate',
        label: 'Duplicate',
        icon: faCopy,
        onClick: () => onDuplicate?.(service)
    };

    const deactivateOption = {
        id: 'deactivate',
        label: isActive ? 'Deactivate' : 'Activate',
        icon: faBan,
        onClick: () => onDeactivate?.(service)
    };

    const options = [editOption, duplicateOption, deactivateOption];

    const petTypesAndLocation = (
        <div className={styles.petAndLocationContainer}>
            <PetTypeTags petTypes={petTypes} />
            {marketplaceName && (
                <div className={styles.marketplaceContainer}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <span className={styles.marketplaceName}>{marketplaceName}</span>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <TableRow className={styles.desktopRow}>
                <TableCell className={styles.nameCell}>
                    <div
                        className={styles.serviceColor}
                        style={{ backgroundColor: service.color || 'transparent' }}
                    ></div>
                    <div className={styles.nameCell}>
                        <p className={styles.serviceName}>{service.name}</p>
                        {petTypesAndLocation}
                    </div>
                </TableCell>

                <TableCell isActions>
                    <ThreeDotsDropdown
                        options={options}
                        direction={isLast ? 'up' : 'down'}
                    />
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={styles.desktopRow}>
            <TableCell className={styles.nameMainCell}>
                <div
                    className={styles.serviceColor}
                    style={{ backgroundColor: service.color || 'transparent' }}
                ></div>
                <div className={styles.nameCell}>
                    <p className={styles.serviceName}>{service.name}</p>
                    {petTypesAndLocation}
                </div>
            </TableCell>

            <TableCell>
                <p>{getRoomName(service.boardingData?.selectedRooms?.[0])}</p>
            </TableCell>

            <TableCell>
                <p>{service.boardingData?.minNights}</p>
            </TableCell>

            <TableCell>
                <p className={styles.price}>{formatPrice(displayPrice)}/night</p>
            </TableCell>

            <TableCell>
                <p>{getTaxesDisplay(service.pricing.taxes)}</p>
            </TableCell>

            <TableCell isActions>
                <ThreeDotsDropdown
                    options={options}
                    direction={isLast ? 'up' : 'down'}
                />
            </TableCell>
        </TableRow>
    );
};

export default BoardingServiceRow;
